import { config } from '../config';
import { log } from '../app/logger';
import { EnvironmentError, MeasurementError, errorMessage } from '../app/errors';
import { appendChunk } from '../data/audioSpikeStore';
import type { AudioPlatform, MediaRecorderLike, MediaStreamLike } from './audioPlatform';
import type { ChunkStat, FinalizeTrigger, GumTrial } from '../types';

/**
 * The spike recorder — build unit U-1's instrument for assumption A-2 and requirement AUD-3.
 *
 * BUILD-UNIT NOTE: this is a declared **thin slice of U-11's `app/recorder.ts`**. It implements the
 * §8.1 stream lifecycle and the §8.5 single-exit `finalizeRecording()` contract because a spike
 * that does not exercise those has not tested AUD-3 — but it deliberately does **not** implement
 * the product recorder: no Observation is written, no command is dispatched, no Flag bar exists,
 * no `FlagResolution`. U-11 owns those and owns the file name `app/recorder.ts`, which is left free.
 *
 * ── The three contracts this file exists to exercise ─────────────────────────
 *
 * §8.1 — the stream is acquired and **held**, not acquired per recording. The design rejects
 *   per-recording acquisition because `getUserMedia` costs 200–800 ms on iOS and FLAG-1 requires a
 *   single tap to start recording immediately; a per-recording acquire loses the opening words of
 *   every flag, which is the part that says what happened. `acquireStream` therefore reports
 *   whether it re-used a held stream, and both costs are measured separately.
 *
 * §8.2 step 2 — every `dataavailable` is persisted **as it arrives**, in its own transaction on the
 *   chunk store only. This is what makes AUD-3's "captured audio is never discarded" true across a
 *   process kill rather than only across a graceful stop.
 *
 * §8.5 — `finalizeRecording()` is a single function and is the **only** exit from the recording
 *   state. Every trigger routes through it: user stop · `VOICE_NOTE_MAX_S` auto-stop ·
 *   `MediaRecorder.onerror` · track `ended` (phone call) · `pagehide` · `visibilitychange → hidden`
 *   · any `Match.state` transition. A fresh `MediaRecorder` is constructed per recording, so
 *   "wedged recorder" is not a reachable state.
 */

const MODULE = 'audio-rec';

// ─────────────────────────────────────────────────────────────────────────────
// Stream lifecycle (§8.1)
// ─────────────────────────────────────────────────────────────────────────────

export interface AcquireResult {
  stream: MediaStreamLike;
  /** `getUserMedia` call → resolved. `0` when a live held stream was re-used. */
  acquireMs: number;
  reusedHeldStream: boolean;
  trackLabel: string | null;
}

export class MicrophoneStream {
  private held: MediaStreamLike | null = null;
  private everAcquired = false;

  constructor(private readonly platform: AudioPlatform) {}

  /** True when a stream is held and every one of its tracks is still `live` (§8.1's readyState guard). */
  isHealthy(): boolean {
    if (!this.held) return false;
    const tracks = this.held.getAudioTracks();
    return tracks.length > 0 && tracks.every((t) => t.readyState === 'live');
  }

  /** Whether this process has ever successfully acquired — distinguishes the first-ever cost. */
  hasEverAcquired(): boolean {
    return this.everAcquired;
  }

  current(): MediaStreamLike | null {
    return this.isHealthy() ? this.held : null;
  }

  /**
   * Returns the held stream if it is healthy, otherwise acquires a new one.
   *
   * A stream whose tracks have gone `ended` — which is what a phone call does — is dropped and
   * re-acquired rather than re-used. §8.1: "a `track.readyState` guard re-acquires the stream on
   * `visibilitychange → visible` and after an interruption, so a post-phone-call recording does not
   * silently capture nothing." That failure mode is the one worth engineering against: a recording
   * that appears to work and contains nothing.
   *
   * @throws {EnvironmentError} when acquisition fails, including a permission denial (AUD-4). The
   *   caller decides what to do; FLAG-4's silent-flag path is U-11's, not the spike's.
   */
  async acquire(): Promise<AcquireResult> {
    if (this.isHealthy() && this.held) {
      const label = this.held.getAudioTracks()[0]?.label ?? null;
      return { stream: this.held, acquireMs: 0, reusedHeldStream: true, trackLabel: label };
    }

    if (this.held) {
      log.warn(MODULE, 'held stream was not live; re-acquiring', {
        states: this.held.getAudioTracks().map((t) => t.readyState),
      });
      this.stopTracks(this.held);
      this.held = null;
    }

    const t0 = this.platform.now();
    let stream: MediaStreamLike;
    try {
      stream = await this.platform.getUserMedia();
    } catch (cause) {
      throw new EnvironmentError(
        `Microphone acquisition failed: ${errorMessage(cause)}. On iOS this is usually a denied permission, or a Safari tab that is not the active one.`,
        { cause },
      );
    }
    const acquireMs = this.platform.now() - t0;

    this.held = stream;
    this.everAcquired = true;
    const label = stream.getAudioTracks()[0]?.label ?? null;
    log.info(MODULE, 'microphone acquired', { acquireMs: Math.round(acquireMs), label });
    return { stream, acquireMs, reusedHeldStream: false, trackLabel: label };
  }

  /**
   * Releases the stream, extinguishing the iOS recording indicator.
   *
   * NFR-5 / §8.1: the stream is never held outside in-match states. The spike releases explicitly
   * so a venue session does not leave the microphone lit after the measurement is over.
   */
  release(): void {
    if (!this.held) return;
    this.stopTracks(this.held);
    this.held = null;
    log.info(MODULE, 'microphone released');
  }

  private stopTracks(stream: MediaStreamLike): void {
    for (const track of stream.getTracks()) {
      try {
        track.stop();
      } catch (cause) {
        // Reported, not swallowed. A track that will not stop leaves the indicator lit, which is
        // worth knowing about at a venue even though nothing can be done about it there.
        log.warn(MODULE, 'track.stop() threw', { error: errorMessage(cause) });
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// One recording
// ─────────────────────────────────────────────────────────────────────────────

export interface StartOptions {
  segmentId: string;
  /** `null` asks the browser to choose — the path AUD-1 describes. */
  requestedMime: string | null;
  /** Auto-stop after this many seconds. Clamped to `VOICE_NOTE_MAX_S` (AUD-2). */
  plannedSeconds: number;
  /** The tap timestamp, for the FLAG-1 tap→recording figure. */
  anchorMs: number;
  /**
   * Defaults to `true`. Set `false` only for the acquisition probe, which starts and immediately
   * finalizes a recorder to time the FLAG-1 path: persisting its throwaway chunk would add
   * IndexedDB work to the interval being measured and leave orphan rows behind a segment that was
   * never a segment. Every real spike segment persists.
   */
  persistChunks?: boolean;
  onChunk?: (stat: ChunkStat) => void;
  onStateChange?: (state: 'recording' | 'finalizing' | 'idle') => void;
}

export interface SegmentOutcome {
  segmentId: string;
  trigger: FinalizeTrigger;
  actualMime: string | null;
  chunks: ChunkStat[];
  totalBytes: number;
  wallDurationMs: number;
  /** anchor → the recorder's `start` event. FLAG-1's number. */
  tapToRecordingMs: number;
  recorderStartMs: number;
}

/** Every listener a recording registers, so teardown cannot miss one. */
interface Teardown {
  (): void;
}

/**
 * A single recording, from `start` to the one exit.
 *
 * Constructed fresh per recording and never re-used — that is the mechanism behind AUD-3's "the
 * recorder is never left in a state where a subsequent record attempt fails". There is no `reset`,
 * no `pause`, and no way to start this object twice.
 */
export class SpikeRecording {
  private recorder: MediaRecorderLike | null = null;
  private chunkIndex = 0;
  private readonly chunks: ChunkStat[] = [];
  private lastChunkAtMs: number | null = null;
  private startedAtMs = 0;
  private recorderStartedAtMs = 0;
  private tapToRecordingMs = 0;
  private recorderStartMs = 0;
  private finalizing: Promise<SegmentOutcome> | null = null;
  private settleFinalized: (outcome: SegmentOutcome) => void = () => undefined;
  private failFinalized: (reason: unknown) => void = () => undefined;
  /**
   * Resolves when *whoever* finalized this recording is done — the user, the auto-stop, a phone
   * call or a page hide. The caller cannot know which of those will happen, so it awaits this
   * rather than the return of its own `finalize` call.
   */
  readonly finalized: Promise<SegmentOutcome> = new Promise<SegmentOutcome>((resolve, reject) => {
    this.settleFinalized = resolve;
    this.failFinalized = reject;
  });
  private teardowns: Teardown[] = [];
  private stopArrived: (() => void) | null = null;
  private stopPromise: Promise<void> | null = null;
  /**
   * Serialises chunk writes into one tail promise.
   *
   * The writes themselves are independent — each chunk has its own primary key — but the *finalize*
   * step has to know they are all done before it assembles, and a chain is the smallest thing that
   * gives that without holding one long transaction open across the whole recording.
   */
  private writeChain: Promise<void> = Promise.resolve();

  constructor(
    private readonly platform: AudioPlatform,
    private readonly stream: MediaStreamLike,
    private readonly options: StartOptions,
  ) {
    // `finalized` is created eagerly but is only awaited by callers that intend to wait for
    // *someone else's* finalize. When the caller instead awaits its own `finalize()` call and that
    // rejects, this promise rejects too with nobody listening — which surfaces as an
    // `unhandledrejection`, which the OBS-1 global handler reports as a fatal on screen at a venue.
    // U-0 hit exactly this shape (`runner.ts` `commitCompleted`). The real error still propagates
    // through `finalize()`; this only marks the duplicate as handled.
    void this.finalized.catch(() => undefined);
  }

  /**
   * Starts recording with `MediaRecorder.start(TIMESLICE_MS)`.
   *
   * Design §8.2 step 1 specifies exactly this: the Observation row is inserted at start, then
   * `MediaRecorder.start(TIMESLICE_MS)`. Writing only at stop cannot satisfy AUD-3, because on a
   * hard kill there is no stop.
   *
   * @throws {MeasurementError} when the recorder cannot be constructed or refuses to start.
   */
  async start(): Promise<void> {
    if (this.recorder) {
      throw new MeasurementError('This recording has already been started; construct a new one.');
    }

    let recorder: MediaRecorderLike;
    try {
      recorder = this.platform.createRecorder(this.stream, this.options.requestedMime);
    } catch (cause) {
      throw new MeasurementError(
        `MediaRecorder could not be constructed${this.options.requestedMime ? ` for ${this.options.requestedMime}` : ''}: ${errorMessage(cause)}`,
        { cause },
      );
    }
    this.recorder = recorder;

    const started = new Promise<void>((resolve) => {
      recorder.onstart = () => resolve();
    });
    this.stopPromise = new Promise<void>((resolve) => {
      this.stopArrived = resolve;
    });

    recorder.ondataavailable = (event) => this.handleChunk(event.data);
    recorder.onstop = () => this.stopArrived?.();
    recorder.onerror = (event) => {
      log.error(MODULE, 'MediaRecorder.onerror', { event: String(event) });
      void this.finalize('recorderError');
    };

    this.registerInterruptionTriggers();

    this.startedAtMs = this.platform.now();
    try {
      recorder.start(config.TIMESLICE_MS);
    } catch (cause) {
      this.teardown();
      throw new MeasurementError(`MediaRecorder.start() threw: ${errorMessage(cause)}`, { cause });
    }

    // Bounded: a `start` event that never arrives must not hang the harness at a venue. The
    // fallback timestamp is the post-call time, which is an upper bound on when recording began.
    await Promise.race([started, delay(config.HARNESS_FRAME_TIMEOUT_MS)]);
    this.recorderStartedAtMs = this.platform.now();
    this.recorderStartMs = this.recorderStartedAtMs - this.startedAtMs;
    this.tapToRecordingMs = this.recorderStartedAtMs - this.options.anchorMs;

    this.options.onStateChange?.('recording');
    log.info(MODULE, 'recording started', {
      segmentId: this.options.segmentId,
      mime: recorder.mimeType,
      timesliceMs: config.TIMESLICE_MS,
      tapToRecordingMs: Math.round(this.tapToRecordingMs),
    });

    this.armAutoStop();
  }

  /** Chunks seen so far. Live, for the on-screen counter. */
  chunkCount(): number {
    return this.chunks.length;
  }

  elapsedMs(): number {
    return this.startedAtMs === 0 ? 0 : this.platform.now() - this.startedAtMs;
  }

  /**
   * **The only exit from the recording state** (AUD-3, design §8.5).
   *
   * Idempotent by construction: the first call captures the promise and every later call — from a
   * `pagehide` racing the user's tap, say — receives the same one with the same trigger. Without
   * that, a phone call arriving as the user taps stop would run two finalizes over one chunk list.
   *
   * Never rejects on a stop that does not arrive. `stop()` may produce a final `dataavailable` or
   * may produce nothing; either way the chunks already persisted are the audio, so a missing stop
   * event costs at most the trailing partial chunk and must not cost the segment.
   */
  finalize(trigger: FinalizeTrigger): Promise<SegmentOutcome> {
    if (this.finalizing) return this.finalizing;
    this.finalizing = this.runFinalize(trigger).then(
      (outcome) => {
        this.settleFinalized(outcome);
        return outcome;
      },
      (reason: unknown) => {
        this.failFinalized(reason);
        throw reason;
      },
    );
    return this.finalizing;
  }

  private async runFinalize(trigger: FinalizeTrigger): Promise<SegmentOutcome> {
    this.options.onStateChange?.('finalizing');
    const recorder = this.recorder;

    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch (cause) {
        log.warn(MODULE, 'MediaRecorder.stop() threw; the persisted chunks are still the audio', {
          error: errorMessage(cause),
        });
      }
      // A stop event that never comes must not strand the segment. Bounded by the same stall
      // detector the rest of the harness uses.
      await Promise.race([
        this.stopPromise ?? Promise.resolve(),
        delay(config.HARNESS_COMMIT_EVENT_TIMEOUT_MS),
      ]);
    }

    // Every chunk write that was issued must land before the chunk list is called complete.
    await this.writeChain.catch(() => undefined);

    this.teardown();
    this.options.onStateChange?.('idle');

    const totalBytes = this.chunks.reduce((sum, c) => sum + c.sizeBytes, 0);
    const outcome: SegmentOutcome = {
      segmentId: this.options.segmentId,
      trigger,
      actualMime: recorder && recorder.mimeType !== '' ? recorder.mimeType : null,
      chunks: [...this.chunks],
      totalBytes,
      wallDurationMs: this.platform.now() - this.startedAtMs,
      tapToRecordingMs: this.tapToRecordingMs,
      recorderStartMs: this.recorderStartMs,
    };

    log.info(MODULE, 'recording finalized', {
      segmentId: outcome.segmentId,
      trigger,
      chunks: outcome.chunks.length,
      totalBytes,
      mime: outcome.actualMime,
    });

    return outcome;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private handleChunk(blob: Blob): void {
    const at = this.platform.now();
    const index = this.chunkIndex++;
    const sinceLastMs = this.lastChunkAtMs === null ? null : at - this.lastChunkAtMs;
    this.lastChunkAtMs = at;

    const stat: ChunkStat = {
      index,
      sinceStartMs: at - this.startedAtMs,
      sinceLastMs,
      sizeBytes: blob.size,
      type: blob.type,
      persistMs: 0,
      persistError: null,
    };
    this.chunks.push(stat);
    this.options.onChunk?.(stat);

    if (this.options.persistChunks === false) return;

    // Persist immediately, in its own transaction, chained so finalize can await all of them.
    this.writeChain = this.writeChain.then(async () => {
      const t0 = this.platform.now();
      try {
        await appendChunk({
          segmentId: this.options.segmentId,
          index,
          blob,
          sizeBytes: blob.size,
          type: blob.type,
          receivedAt: Date.now(),
        });
        stat.persistMs = this.platform.now() - t0;
      } catch (cause) {
        stat.persistMs = this.platform.now() - t0;
        stat.persistError = errorMessage(cause);
        // Surfaced on the segment, not thrown: one unwritable chunk is a hole in this recording,
        // and losing the rest of the recording to it would be strictly worse (NFR-8).
        log.error(MODULE, 'chunk could not be persisted', {
          segmentId: this.options.segmentId,
          index,
          error: stat.persistError,
        });
      }
    });
  }

  /**
   * Wires every AUD-3 trigger to the one exit.
   *
   * §8.5 enumerates them: user stop · `VOICE_NOTE_MAX_S` auto-stop · `MediaRecorder.onerror` ·
   * track `ended` (phone call) · `pagehide` · `visibilitychange → hidden` · any `Match.state`
   * transition. The first three are wired at their sources; the rest are here. The state-transition
   * trigger has no source in a spike with no state machine — the caller invokes `finalize` — and
   * U-11 wires it to the command bus.
   */
  private registerInterruptionTriggers(): void {
    for (const track of this.stream.getAudioTracks()) {
      const onEnded = () => {
        log.warn(MODULE, 'audio track ended mid-recording — this is the phone-call path', {
          segmentId: this.options.segmentId,
        });
        void this.finalize('trackEnded');
      };
      track.addEventListener('ended', onEnded);
      this.teardowns.push(() => track.removeEventListener('ended', onEnded));
    }

    const w = globalThis as unknown as {
      addEventListener?: (t: string, l: () => void) => void;
      removeEventListener?: (t: string, l: () => void) => void;
      document?: { visibilityState?: string };
    };
    if (typeof w.addEventListener !== 'function') return;

    const onPageHide = () => {
      void this.finalize('pageHide');
    };
    const onVisibility = () => {
      if (w.document?.visibilityState === 'hidden') void this.finalize('visibilityHidden');
    };

    w.addEventListener('pagehide', onPageHide);
    w.addEventListener('visibilitychange', onVisibility);
    this.teardowns.push(() => w.removeEventListener?.('pagehide', onPageHide));
    this.teardowns.push(() => w.removeEventListener?.('visibilitychange', onVisibility));
  }

  /**
   * AUD-2 — auto-stop at the planned length, never later than `VOICE_NOTE_MAX_S`.
   *
   * Distinct triggers so the report can tell "the spike's 12 s elapsed" from "the product's 60 s
   * ceiling fired", which are different behaviours being verified.
   */
  private armAutoStop(): void {
    const plannedMs = Math.min(this.options.plannedSeconds, config.VOICE_NOTE_MAX_S) * 1000;
    const capMs = config.VOICE_NOTE_MAX_S * 1000;
    const trigger: FinalizeTrigger = plannedMs >= capMs ? 'voiceNoteMax' : 'plannedDuration';

    const handle = setTimeout(() => {
      void this.finalize(trigger);
    }, plannedMs);
    this.teardowns.push(() => clearTimeout(handle));
  }

  private teardown(): void {
    for (const undo of this.teardowns) {
      try {
        undo();
      } catch (cause) {
        log.warn(MODULE, 'teardown step threw', { error: errorMessage(cause) });
      }
    }
    this.teardowns = [];
    if (this.recorder) {
      this.recorder.ondataavailable = null;
      this.recorder.onstart = null;
      this.recorder.onstop = null;
      this.recorder.onerror = null;
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─────────────────────────────────────────────────────────────────────────────
// getUserMedia acquisition cost (§8.1, FLAG-1)
// ─────────────────────────────────────────────────────────────────────────────

export interface GumPassOptions {
  trials: number;
  mic: MicrophoneStream;
  platform: AudioPlatform;
  /** Whether to release between trials. `true` measures cold, `false` measures warm re-use. */
  releaseBetween: boolean;
  onProgress?: (done: number, total: number) => void;
}

/**
 * Measures acquisition, cold or warm, as a distribution.
 *
 * The **cold** pass releases the stream between trials, so each measures a genuine `getUserMedia`.
 * The **warm** pass leaves it held and measures the full tap→recording path instead, because on the
 * warm path acquisition is not the cost — constructing and starting a recorder is, and that is what
 * FLAG-1 actually pays for.
 *
 * The very first acquisition of a page load is labelled `firstEver` and kept out of the cold
 * distribution: it may include a permission prompt, which is a one-off the user sees at Pre-Match
 * (§8.1), not something a Flag tap pays.
 *
 * @throws {MeasurementError} if `trials` is outside its configured bounds.
 */
export async function measureAcquisition(options: GumPassOptions): Promise<GumTrial[]> {
  const { trials, mic, platform } = options;
  if (trials < config.GUM_TRIALS_MIN || trials > config.GUM_TRIALS_MAX) {
    throw new MeasurementError(
      `Acquisition trial count ${trials} is outside [${config.GUM_TRIALS_MIN}, ${config.GUM_TRIALS_MAX}].`,
    );
  }

  const out: GumTrial[] = [];

  for (let i = 0; i < trials; i++) {
    if (options.releaseBetween) mic.release();

    const wasFirst = !mic.hasEverAcquired();
    const anchor = platform.now();

    try {
      const acquired = await mic.acquire();
      const kind: GumTrial['kind'] = wasFirst
        ? 'firstEver'
        : acquired.reusedHeldStream
          ? 'warm'
          : 'cold';

      // The recorder is constructed and started, then immediately finalized: the FLAG-1 figure is
      // tap → *recording*, and a stream that is acquired but cannot be recorded from has not
      // started recording. A ~0-length segment is discarded; only its timing is kept.
      const probeId = `gum-${Date.now().toString(36)}-${i}`;
      const recording = new SpikeRecording(platform, acquired.stream, {
        segmentId: probeId,
        requestedMime: null,
        plannedSeconds: config.AUDIO_SPIKE_SEGMENT_MIN_S,
        anchorMs: anchor,
        persistChunks: false,
      });
      await recording.start();
      const outcome = await recording.finalize('userStop');

      out.push({
        index: i,
        kind,
        acquireMs: acquired.acquireMs,
        recorderStartMs: outcome.recorderStartMs,
        tapToRecordingMs: outcome.tapToRecordingMs,
        trackLabel: acquired.trackLabel,
        error: null,
      });
    } catch (cause) {
      // Recorded as a failed trial, never as a fast one. A denied microphone is the AUD-4 path and
      // belongs in the report as a denial, not as 0 ms.
      out.push({
        index: i,
        kind: wasFirst ? 'firstEver' : options.releaseBetween ? 'cold' : 'warm',
        acquireMs: Number.NaN,
        recorderStartMs: Number.NaN,
        tapToRecordingMs: Number.NaN,
        trackLabel: null,
        error: errorMessage(cause),
      });
      log.exception(MODULE, 'acquisition trial failed', cause, { index: i });
    }

    options.onProgress?.(i + 1, trials);
  }

  return out;
}
